import { Applicant, SegmentType } from '../../../../../utils/types/JobOpenings';

interface I2DChartOptions {
  candidates: Applicant[];
  useSkillPercentage: boolean;
  skillValue: number[];
  experienceValue: number[];
  markLines: boolean;
  nineBox: boolean;
  segmentedData: SegmentType[][];
  selectedApplicantsForEmail?: Applicant[];
  isApplicantSelected?: (applicant: Applicant) => boolean;
  currentTab?: string;
  showHeadshots?: boolean;
  filteredSkills?: string[];
  compact?: boolean;
}

// Types for better code organization
type StatusColorMap = Record<string, string>;
type CandidateSymbol = string;

// Constants
const STATUS_COLORS: StatusColorMap = {
  'Unsuccessful-Closed': '#dc2626',
  'Unsuccessful-Pending': '#ea580c',
  Shortlisted: '#16a34a',
  Suitable: '#2563eb',
  Outstanding: '#9333ea',
  New: '#06b6d4',
  'Auto-Assessment': '#eab308',
  Unknown: '#6b7280',
};

// Utility functions
const getInitials = (name: string): string => {
  const parts = name.split(' ').filter(Boolean);
  return parts.length > 1
    ? parts[0][0].toUpperCase() + parts[parts.length - 1][0].toUpperCase()
    : parts[0][0].toUpperCase();
};

const hasValidHeadshot = (symbolUrl: string | undefined): boolean => {
  return !!(
    symbolUrl &&
    symbolUrl.trim() !== '' &&
    symbolUrl !== 'circle' &&
    !symbolUrl.includes('undefined') &&
    !symbolUrl.includes('null')
  );
};

const createInitialsSymbol = (
  initials: string,
  bgColor: string,
): CandidateSymbol => {
  try {
    const safeInitials = initials.replace(/[<>&"']/g, '');
    const svg = `<svg width="32" height="32" xmlns="http://www.w3.org/2000/svg"><circle cx="16" cy="16" r="16" fill="${bgColor}" stroke="#ffffff" stroke-width="2"/><text x="16" y="21" text-anchor="middle" fill="#ffffff" font-family="Arial, sans-serif" font-size="11" font-weight="600">${safeInitials}</text></svg>`;
    return 'image://data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
  } catch {
    return 'circle';
  }
};

const getCandidateSymbol = (
  candidate: Applicant,
  isSelected: boolean,
  showHeadshots: boolean = true,
): CandidateSymbol => {
  const applicantStatus = candidate.applicantStatus || 'Unknown';

  // Priority 1: Use actual headshot if available AND showHeadshots is enabled
  if (showHeadshots && hasValidHeadshot(candidate.symbol)) {
    return candidate.symbol!;
  }

  // Priority 2: For all other cases, show initials (no icons)
  const initials = getInitials(candidate.name);
  let bgColor = STATUS_COLORS[applicantStatus] || STATUS_COLORS['Unknown'];

  // Handle pending color selection for initials
  if (applicantStatus === 'Unsuccessful-Pending') {
    bgColor = isSelected ? '#eab308' : '#ea580c';
  }

  return createInitialsSymbol(initials, bgColor);
};

export const Get2DChartOptions = ({
  candidates,
  useSkillPercentage,
  skillValue,
  experienceValue,
  markLines,
  nineBox,
  segmentedData,
  isApplicantSelected,
  showHeadshots = true,
  filteredSkills,
  compact = false,
}: I2DChartOptions) => {
  return {
    backgroundColor: '#ffffff',
    title: [
      {
        text: candidates.length + ' Candidates',
        left: 'center',
        top: compact ? 12 : 18,
        textStyle: {
          fontSize: compact ? 20 : 26,
          fontWeight: 600,
          color: '#0f172a',
          fontFamily: '-apple-system, SF Pro Display, system-ui, sans-serif',
          letterSpacing: '0.2px',
          textShadow: '1px 1px 3px rgba(15, 23, 42, 0.08)',
        },
      },
      {
        text: 'Interactive Candidate Analysis',
        left: 'center',
        top: compact ? 42 : 52,
        textStyle: {
          fontSize: compact ? 12 : 14,
          color: '#64748b',
          fontWeight: 400,
          fontFamily: '-apple-system, SF Pro Text, system-ui, sans-serif',
          letterSpacing: '0.1px',
          textShadow: '1px 1px 2px rgba(100, 116, 139, 0.06)',
        },
      },
    ],
    grid: {
      top: compact ? 92 : 112,
      right: compact ? 24 : 64,
      bottom: compact ? 56 : 76,
      left: compact ? 44 : 72,
      containLabel: true,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: 'rgba(148, 163, 184, 0.1)',
      borderWidth: 1,
      show: true,
      shadowColor: 'rgba(15, 23, 42, 0.05)',
      shadowBlur: 8,
      shadowOffsetY: 1,
    },
    // Starling perf: disable heavy animation for 820 points, enable progressive canvas rendering
    animation: candidates.length < 300,
    animationDuration: 200,
    animationEasing: 'cubicOut',
    progressive: 400,
    progressiveThreshold: 300,
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: 'rgba(148, 163, 184, 0.2)',
      borderWidth: 1,
      extraCssText:
        'backdrop-filter: blur(8px); box-shadow: 0 8px 24px rgba(148, 163, 184, 0.2);',
      textStyle: {
        color: '#0f172a',
        fontFamily: '-apple-system, SF Pro Text, system-ui, sans-serif',
        fontSize: 13,
        lineHeight: 1.6,
      },
      formatter: (params: any) => {
        const { data, value } = params;
        const allData = candidates;

        // Step 1: Find all overlapping candidates
        const overlapping = allData.filter(
          (item: any) =>
            item.value[0] === value[0] && item.value[1] === value[1],
        );

        // Step 2: If only one candidate, use Tooltip 1 format
        if (overlapping.length === 1) {
          const matchedSkillsArray = Array.isArray(data?.matchedSkills)
            ? data.matchedSkills
            : typeof data?.matchedSkills === 'string'
              ? data.matchedSkills.split(',').map((s: string) => s.trim())
              : [];

          // Filter skills if filteredSkills is provided
          const displayedSkills =
            filteredSkills && filteredSkills.length > 0
              ? matchedSkillsArray.filter((skill: string) =>
                filteredSkills.some(
                  (fs) => fs.toLowerCase() === skill.toLowerCase(),
                ),
              )
              : matchedSkillsArray;

          const matchedSkillsHTML = displayedSkills.length
            ? `<ul style="margin: 4px 0 0 0; padding-left: 18px; color: #333;">${displayedSkills
              .map((skill: string) => `<li>${skill}</li>`)
              .join('')}</ul>`
            : 'N/A';

          // Handle profile image/initials for unsuccessful candidates (works across all tabs)
          let profileDisplay = '';
          const isSelected =
            isApplicantSelected &&
            isApplicantSelected(data) &&
            data.applicantStatus === 'Unsuccessful-Pending';
          const applicantStatus = data.applicantStatus || data.status;

          if (applicantStatus === 'Unsuccessful-Closed') {
            // Always show initials in RED for closed unsuccessful candidates
            profileDisplay = `<div style="width: 60px; height: 60px; background: #dc2626; border-radius: 50%; margin: 10px auto; display: flex; align-items: center; justify-content: center; font-size: 20px; color: white; font-weight: bold;">
                                ${getInitials(data?.name || 'Unknown')}
                              </div>`;
          } else if (applicantStatus === 'Unsuccessful-Pending') {
            if (isSelected) {
              // Show initials in YELLOW when selected
              profileDisplay = `<div style="width: 60px; height: 60px; background: #eab308; border-radius: 50%; margin: 10px auto; display: flex; align-items: center; justify-content: center; font-size: 20px; color: white; font-weight: bold;">
                                  ${getInitials(data?.name || 'Unknown')}
                                </div>`;
            } else {
              // Show headshot if available, otherwise initials
              profileDisplay =
                data.symbol === 'circle'
                  ? `<div style="width: 60px; height: 60px; background: #ddd; border-radius: 50%; margin: 10px auto; display: flex; align-items: center; justify-content: center; font-size: 20px;">
                     ${getInitials(data?.name || 'Unknown')}
                   </div>`
                  : `<img src="${
                    data.symbol?.replace('image://', '') || ''
                  }" alt="Profile" style="width: 60px; height: 60px; border-radius: 50%; margin: 10px auto; display: block;" />`;
            }
          } else {
            // Default logic for other statuses
            profileDisplay =
              data.symbol === 'circle'
                ? `<div style="width: 60px; height: 60px; background: #ddd; border-radius: 50%; margin: 10px auto; display: flex; align-items: center; justify-content: center; font-size: 20px;">
                   ${getInitials(data?.name || 'Unknown')}
                 </div>`
                : `<img src="${
                  data.symbol?.replace('image://', '') || ''
                }" alt="Profile" style="width: 60px; height: 60px; border-radius: 50%; margin: 10px auto; display: block;" />`;
          }

          return `
            <div style="font-family: 'Segoe UI', sans-serif; font-size: 13px; color: #333; padding: 6px 0; line-height: 1.6;">
                ${profileDisplay}
                <div style="font-size: 15px; font-weight: 600; margin-bottom: 4px; text-align: center;">${
  data?.name || 'Unknown'
}</div>
                <div><span style="color: #888;">Experience:</span> <span style="font-weight: 500;">${
  data?.experience ?? 'N/A'
} years</span></div>
                <div><span style="color: #888;">Skills:</span> <span style="font-weight: 500;">${
  typeof data?.noOfSkills === 'number'
    ? useSkillPercentage
      ? data.noOfSkills.toFixed(2) + '%'
      : data.noOfSkills.toString()
    : 'N/A'
}</span></div>
                <div><span style="color: #888;">Behavioural Attributes:</span> <span style="font-weight: 500;">${
  data?.behavioralAttributes?.join(', ') ?? 'N/A'
}</span></div>
                <div><span style="color: #888;">Keywords:</span> <span style="font-weight: 500;">${
  data?.keywords?.join(', ') ?? 'N/A'
}</span></div>
                <div><span style="color: #888;">Matched Skills:</span> ${matchedSkillsHTML}</div>
            </div>
        `;
        }

        // Step 3: Tooltip 2 format for multiple overlapping candidates

        let tooltipContent =
          '<div style="padding: 8px 12px; font-family: \'Segoe UI\', sans-serif; font-size: 13px;">';
        tooltipContent += `<div style="margin-bottom: 8px; font-weight: 500;">Total of ${overlapping.length} candidates</div>`;

        const displayLimit = 8;
        const shownCandidates = overlapping.slice(0, displayLimit);

        shownCandidates.forEach((item: any) => {
          // Always show initials for all candidates, no user icons
          const applicantStatus = item.applicantStatus || item.status;
          const bgColor =
            STATUS_COLORS[applicantStatus] || STATUS_COLORS['Unknown'];

          const symbolDisplay = `<div style="width: 36px; height: 36px; background: ${bgColor}; border-radius: 50%; margin-right: 8px; display: flex; align-items: center; justify-content: center; font-size: 14px; color: white; font-weight: bold;">
                                  ${getInitials(item.name)}
                               </div>`;

          tooltipContent += `
            <div style="display: flex; align-items: center; margin-bottom: 8px;">
                ${symbolDisplay}
                <strong>${item.name}</strong>
            </div>
        `;
        });

        const remainingCount = overlapping.length - shownCandidates.length;
        if (remainingCount > 0) {
          tooltipContent += `
            <div style="margin-top: 4px; color: #666; font-size: 12px;">
                and ${remainingCount} more...
            </div>
        `;
        }

        tooltipContent += '</div>';
        return tooltipContent;
      },
    },

    xAxis: {
      name: useSkillPercentage
        ? 'Weighted Skill Score (%)'
        : 'Number of Skills',
      min: skillValue[0],
      max: skillValue[1],
      splitLine: {
        show: true,
        lineStyle: {
          color: 'rgba(71, 85, 105, 0.2)',
          width: 1,
          type: 'solid',
        },
      },
      axisLine: {
        show: true,
        onZero: true,
        lineStyle: {
          color: 'rgba(51, 65, 85, 0.6)',
          width: 2,
        },
      },
      axisTick: {
        show: true,
        length: 6,
        lineStyle: {
          color: 'rgba(51, 65, 85, 0.6)',
          width: 1,
        },
      },
      axisLabel: {
        show: true,
        color: '#1e293b',
        fontSize: compact ? 10 : 12,
        fontFamily: '-apple-system, SF Pro Text, system-ui, sans-serif',
        margin: compact ? 8 : 14,
        fontWeight: 500,
        padding: compact ? [6, 4] : [10, 8],
        borderRadius: 4,
        shadowBlur: 4,
        shadowOffsetY: 1,
        formatter: (value: number) =>
          useSkillPercentage ? value.toFixed(2) + '%' : value.toString(),
        width: 60,
        height: 24,
        align: 'center',
      },
      nameLocation: 'middle',
      nameGap: compact ? 32 : 44,
      nameTextStyle: {
        color: '#0f172a',
        fontSize: compact ? 12 : 14,
        fontWeight: 600,
        fontFamily: '-apple-system, SF Pro Display, system-ui, sans-serif',
        letterSpacing: '0.3px',
        // backgroundColor: "rgba(255, 255, 255, 0.95)",
        padding: compact ? [6, 4] : [12, 8],
        borderRadius: 4,
        shadowBlur: 4,
        shadowColor: 'rgba(0, 0, 0, 0.05)',
      },
    },
    yAxis: {
      name: 'Experience (Years)',
      min: experienceValue[0],
      max: experienceValue[1],
      splitLine: {
        show: true,
        lineStyle: {
          color: 'rgba(71, 85, 105, 0.2)',
          width: 1,
          type: 'solid',
        },
      },
      axisLine: {
        show: true,
        lineStyle: {
          color: 'rgba(51, 65, 85, 0.6)',
          width: 2,
        },
      },
      axisTick: {
        show: true,
        length: 6,
        lineStyle: {
          color: 'rgba(51, 65, 85, 0.6)',
          width: 1,
        },
      },
      axisLabel: {
        show: true,
        color: '#000000',
        fontSize: compact ? 10 : 12,
        fontFamily: '-apple-system, SF Pro Text, system-ui, sans-serif',
        margin: compact ? 8 : 16,
        fontWeight: 600,
        padding: compact ? [2, 4] : [4, 8],
        borderRadius: 4,
        shadowBlur: 8,
        shadowColor: 'rgba(0, 0, 0, 0.15)',
        shadowOffsetY: 2,
        formatter: (value: number) => value.toString(),
        width: 40,
        height: 24,
        align: 'center',
        rich: {
          value: {
            backgroundColor: '#ffffff',
            borderColor: '#e2e8f0',
            borderWidth: 1,
            borderRadius: 4,
            padding: [4, 6],
          },
        },
      },
      nameLocation: 'middle',
      nameGap: compact ? 34 : 44,
      nameTextStyle: {
        color: '#0f172a',
        fontSize: compact ? 12 : 14,
        fontWeight: 600,
        fontFamily: '-apple-system, SF Pro Display, system-ui, sans-serif',
        letterSpacing: '0.3px',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        padding: compact ? [2, 4] : [4, 8],
        borderRadius: 4,
        shadowBlur: 4,
        shadowColor: 'rgba(0, 0, 0, 0.05)',
      },
    },
    series: [
      {
        type: 'scatter',
        symbolSize: compact ? 26 : 32,
        itemStyle: {
          color: (params: any) => {
            const point = params.data;
            const overlappingPoints = candidates.filter(
              (c) =>
                c.value[0] === point.value[0] && c.value[1] === point.value[1],
            ).length;

            // Check if this candidate is selected for email (only for pending candidates)
            const isSelected =
              isApplicantSelected &&
              isApplicantSelected(point) &&
              point.applicantStatus === 'Unsuccessful-Pending';
            const applicantStatus = point.applicantStatus || point.status;

            // Handle different colors for unsuccessful candidate statuses (works across all tabs)
            if (applicantStatus === 'Unsuccessful-Closed') {
              // RED color for closed unsuccessful candidates (always, regardless of selection)
              return {
                type: 'radial',
                x: 0.5,
                y: 0.5,
                r: 0.5,
                colorStops: [
                  {
                    offset: 0,
                    color: '#fca5a5', // Light red
                  },
                  {
                    offset: 1,
                    color: '#dc2626', // Darker red
                  },
                ],
              };
            } else if (
              applicantStatus === 'Unsuccessful-Pending' &&
              isSelected
            ) {
              // YELLOW color for selected pending unsuccessful candidates
              return {
                type: 'radial',
                x: 0.5,
                y: 0.5,
                r: 0.5,
                colorStops: [
                  {
                    offset: 0,
                    color: '#fcd34d', // Light yellow
                  },
                  {
                    offset: 1,
                    color: '#eab308', // Darker yellow
                  },
                ],
              };
            }

            // Default color logic for selected candidates on other tabs
            if (isSelected) {
              return {
                type: 'radial',
                x: 0.5,
                y: 0.5,
                r: 0.5,
                colorStops: [
                  {
                    offset: 0,
                    color: '#fbbf24', // Golden yellow for selected
                  },
                  {
                    offset: 1,
                    color: '#f59e0b', // Darker golden for selected
                  },
                ],
              };
            }

            // Default color for all tabs
            return {
              type: 'radial',
              x: 0.5,
              y: 0.5,
              r: 0.5,
              colorStops: [
                {
                  offset: 0,
                  color: overlappingPoints > 1 ? '#c7d2fe' : '#818cf8',
                },
                {
                  offset: 1,
                  color: overlappingPoints > 1 ? '#4f46e5' : '#3730a3',
                },
              ],
            };
          },
          borderColor: (params: any) => {
            const point = params.data;
            const isSelected =
              isApplicantSelected &&
              isApplicantSelected(point) &&
              point.applicantStatus === 'Unsuccessful-Pending';
            const applicantStatus = point.applicantStatus || point.status;

            // Handle border colors for unsuccessful candidate statuses (works across all tabs)
            if (applicantStatus === 'Unsuccessful-Closed') {
              return '#ffffff'; // White border for closed unsuccessful candidates
            } else if (
              applicantStatus === 'Unsuccessful-Pending' &&
              isSelected
            ) {
              return '#f59e0b'; // Yellow border if selected
            }

            return isSelected ? '#f59e0b' : '#ffffff';
          },
          borderWidth: (params: any) => {
            const point = params.data;
            const isSelected =
              isApplicantSelected &&
              isApplicantSelected(point) &&
              point.applicantStatus === 'Unsuccessful-Pending';
            return isSelected ? 4 : 2;
          },
          shadowColor: 'rgba(99, 102, 241, 0.15)',
          shadowBlur: 6,
          shadowOffsetY: 2,
        },
        emphasis: {
          itemStyle: {
            color: {
              type: 'radial',
              x: 0.5,
              y: 0.5,
              r: 0.5,
              colorStops: [
                {
                  offset: 0,
                  color: '#a5b4fc',
                },
                {
                  offset: 1,
                  color: '#6366f1',
                },
              ],
            },
            borderColor: '#ffffff',
            borderWidth: 3,
            shadowColor: 'rgba(99, 102, 241, 0.3)',
            shadowBlur: 8,
            shadowOffsetY: 2,
          },
          scale: true,
          scaleSize: 1.1,
        },
        data: candidates.map((candidate) => {
          const applicantStatus = candidate.applicantStatus || 'Unknown';
          const isSelected =
            isApplicantSelected &&
            isApplicantSelected(candidate) &&
            applicantStatus === 'Unsuccessful-Pending';

          const finalSymbol = getCandidateSymbol(
            candidate,
            isSelected || false,
            showHeadshots,
          );

          return {
            ...candidate,
            name: candidate.name,
            value: candidate.value,
            noOfSkills: candidate.value[0],
            experience: candidate.value[1],
            symbol: finalSymbol,
            itemStyle: {
              color: 'transparent',
            },
            label: {
              show: false,
            },
            matchedSkills: candidate.skills,
            behavioralAttributes: candidate.behavioralAttributes,
            matchedBehavior: candidate.matchedBehavior,
            keywords: candidate.keywords || [],
          };
        }),
        markLine:
          markLines && nineBox
            ? {
              silent: true,
              lineStyle: {
                color: 'grey',
              },
              data: [
                {
                  xAxis:
                      skillValue[0] + (1 / 3) * (skillValue[1] - skillValue[0]),
                },
                {
                  xAxis:
                      skillValue[0] + (2 / 3) * (skillValue[1] - skillValue[0]),
                },
                {
                  yAxis:
                      experienceValue[0] +
                      (1 / 3) * (experienceValue[1] - experienceValue[0]),
                },
                {
                  yAxis:
                      experienceValue[0] +
                      (2 / 3) * (experienceValue[1] - experienceValue[0]),
                },
              ],
              symbol: ['none'],
            }
            : {},
        markArea: nineBox
          ? {
            silent: false,
            tooltip: { show: false },
            itemStyle: {
              opacity: 0.1,
            },
            label: {
              position: 'inside',
              align: 'center',
              verticalAlign: 'middle',
              fontSize: compact ? 10 : 12,
              fontWeight: 'bold',
              color: 'darkgrey',
            },
            emphasis: {
              label: {
                position: 'inside',
                align: 'center',
                fontSize: compact ? 11 : 13,
                fontWeight: 'bold',
                color: 'grey',
              },
            },
            data: segmentedData,
          }
          : {},
      },
    ],
  };
};

// Add click event handler for candidate selection
export const getChartEvents = (
  onApplicantSelection?: (applicant: any) => void,
) => ({
  click: (params: any) => {
    // Enable click selection on all tabs for email functionality
    if (onApplicantSelection && params.componentType === 'series') {
      const clickedApplicant = params.data;
      onApplicantSelection(clickedApplicant);
    }
  },
});
